import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackGA } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, CheckCircle2, MapPin, DollarSign, Sparkles,
  Wrench, ShieldCheck, Clock, AlertTriangle, Loader2,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { serviceLabel } from "@/lib/pricing";

const fmt$ = (v) => "$" + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/** Convert "window_rescreen" → "Window Rescreen" (Title Case fallback). */
function prettyServiceLabel(key) {
  const mapped = serviceLabel(key);
  // serviceLabel returns the key unchanged when it's not in the map — Title Case the snake_case.
  if (!mapped || mapped === key) {
    return (key || "screen")
      .split("_")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
      .join(" ")
      .trim() || "Screen";
  }
  return mapped;
}

/** Sum quantity across all services on the lead. */
function totalUnits(lead) {
  return (lead?.services || []).reduce((sum, s) => sum + Number(s.quantity || 0), 0);
}

/** Short, headline-friendly scope: e.g. "3 window rescreens". */
function servicesScopeHeadline(lead) {
  const services = lead?.services || [];
  if (!services.length) return "a screen job";
  // Single service type → "N <label>"
  if (services.length === 1) {
    const s = services[0];
    const qty = Number(s.quantity || 0);
    const label = prettyServiceLabel(s.service_key).toLowerCase();
    if (qty <= 0) return label;
    return `${qty} ${qty === 1 ? label : pluralizeService(label)}`;
  }
  // Multiple service types → "N screens" (a "job" implies a site visit — one homeowner with multiple services is still one job)
  const total = totalUnits(lead);
  return total > 0 ? `${total} screens` : "multiple screens";
}

/** Multi-line detail for the Job Scope tile. */
function servicesScopeDetail(lead) {
  const services = lead?.services || [];
  if (!services.length) return "Window screens";
  return services
    .map((s) => {
      const qty = Number(s.quantity || 0);
      const label = prettyServiceLabel(s.service_key);
      return qty > 1 ? `${qty}× ${label}` : label;
    })
    .join(" · ");
}

function pluralizeService(label) {
  // "window screen rescreen" → "window screen rescreens"; idempotent for already-plural
  if (/s$/.test(label)) return label;
  return label + "s";
}

export default function Invite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    api.get(`/public/invites/${token}`)
      .then((r) => {
        if (cancelled) return;
        setState({ loading: false, data: r.data, error: null });
        trackGA("invite_page_viewed", { claimed: !!r.data?.lead?.is_claimed }, { once: true });
      })
      .catch((e) => { if (!cancelled) setState({ loading: false, data: null, error: formatApiErrorDetail(e.response?.data?.detail) || "Invite link is no longer valid." }); });
    return () => { cancelled = true; };
  }, [token]);

  const claim = () => {
    trackGA("invite_claim_clicked", { claimed: !!state.data?.lead?.is_claimed });
    nav(`/signup?invite_token=${encodeURIComponent(token)}`);
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center">
        <div className="text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your invite…
        </div>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-[#FBFAF6]">
        <Nav />
        <main className="max-w-2xl mx-auto px-6 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-700 mb-6">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-testid="invite-error-title">
            This invite isn't valid
          </h1>
          <p className="text-slate-600 mb-8">{state.error || "The link may have expired."}</p>
          <Link to="/signup">
            <Button className="rounded-xl bg-[#C75D33] hover:bg-[#A84C28] text-white h-12 px-6" data-testid="invite-error-signup-btn">
              Start a $1 trial anyway <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  const { lead, invite } = state.data;
  const claimed = invite?.status === "claimed" || lead?.is_claimed;

  return (
    <div className="min-h-screen bg-[#FBFAF6]">
      <Nav />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* LEFT: the lead preview */}
          <section className="lg:col-span-7">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/95 text-amber-950 text-[11px] font-extrabold uppercase tracking-widest mb-5" data-testid="invite-tagline-pill">
              <Sparkles className="w-3.5 h-3.5" /> A homeowner is waiting for your call
            </div>

            <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]" data-testid="invite-h1">
              {lead.first_name} wants <span className="text-[#C75D33]">{servicesScopeHeadline(lead)}</span> in {lead.zip_code || "your area"}.
            </h1>
            <p className="text-sm text-slate-500 mt-2">Customer's estimated quote: <strong className="text-slate-700">{fmt$(lead.estimate_total)}</strong></p>

            <p className="text-lg text-slate-600 mt-5 max-w-xl leading-relaxed">
              We picked you because you serve this area. <strong className="text-slate-900">Claim it free — no credit card needed.</strong> Sign up in under 2 minutes and the homeowner's full contact info lands in your dashboard.
            </p>

            {/* Lead card */}
            <div className="mt-8 bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-7" data-testid="invite-lead-card">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#C75D33] mb-1">Lead preview</div>
              <div className="font-display font-bold text-xl text-slate-900">
                {lead.first_name} · {lead.zip_code || "ZIP withheld"}{lead.state ? " · " + lead.state : ""}
              </div>
              <p className="text-xs text-slate-500 mt-1">Full contact details (last name, phone, email) appear in your dashboard once you sign up.</p>

              <div className="grid sm:grid-cols-3 gap-3 mt-5">
                <Tile icon={MapPin} label="Location" value={`${lead.zip_code || "—"}${lead.state ? " · " + lead.state : ""}`} />
                <Tile icon={Wrench} label="Job scope" value={servicesScopeDetail(lead)} cap />
                <Tile icon={DollarSign} label="Customer quote" value={fmt$(lead.estimate_total)} accent />
              </div>

              {claimed ? (
                <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="invite-already-claimed">
                  <div className="inline-flex items-center gap-2 font-bold mb-1"><AlertTriangle className="w-4 h-4" /> Already claimed</div>
                  Another company beat you to this one. You can still create your free account — when you're ready, opt in to the marketplace and we'll route the next matching {lead.zip_code || "local"} lead to you.
                  <div className="mt-3">
                    <Button onClick={claim} className="rounded-xl bg-[#0F172A] hover:bg-black text-white h-10 px-5" data-testid="invite-claimed-signup-btn">
                      Create my free account <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-6 rounded-2xl bg-slate-900 text-white p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">Your move</div>
                    <div className="font-display font-bold text-lg mt-0.5">Claim this lead — free</div>
                    <div className="text-xs opacity-80 mt-0.5">No credit card. No subscription. Sign up and it's yours.</div>
                  </div>
                  <Button onClick={claim} className="rounded-xl bg-[#C75D33] hover:bg-[#A84C28] text-white h-12 px-6 shrink-0" data-testid="invite-claim-btn">
                    Claim this lead <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* RIGHT: the trial pitch */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-br from-[#0F172A] to-black text-white p-6 sm:p-7">
                <div className="text-xs font-bold uppercase tracking-widest text-white/85 mb-1.5">What you're getting</div>
                <h2 className="font-display text-2xl font-bold leading-tight">A real homeowner. Yours, free.</h2>
              </div>
              <div className="p-6 sm:p-7 space-y-4">
                <Benefit icon={CheckCircle2} title="This lead, free — no card needed" desc="Sign up and it's routed to your dashboard with the homeowner's full contact info." />
                <Benefit icon={Clock} title="Live in under 2 minutes" desc="Just your business name, email, and password. That's it." />
                <Benefit icon={Sparkles} title="Want more leads later?" desc="Upgrade anytime to your founder rate ($1 for 30 days, then $97/mo) — unlocks your public quote page and lets you opt in to marketplace leads (you pay a small % of each quote you receive, capped at $50)." />
                <Benefit icon={ShieldCheck} title="No commitment" desc="Your data and brand stay yours. Walk away anytime — we don't sell your customers." />
              </div>
              <div className="px-6 sm:px-7 pb-6">
                <Button
                  onClick={claim}
                  className="w-full rounded-xl bg-[#C75D33] hover:bg-[#A84C28] text-white h-12"
                  data-testid="invite-claim-sidebar-btn"
                >
                  {claimed ? (
                    <>Create my free account <ArrowRight className="w-4 h-4 ml-2" /></>
                  ) : (
                    <>Claim this lead — free <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
                {claimed && (
                  <p className="text-[11px] text-slate-500 text-center mt-2" data-testid="invite-claimed-note">
                    This lead is already claimed, but your account is still free to create.
                  </p>
                )}
                <p className="text-[11px] text-slate-500 text-center mt-3">Already have an account? <Link to="/login" className="font-bold text-slate-900 hover:text-[#C75D33]">Sign in</Link> and contact support to attach this lead.</p>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function Nav() {
  return (
    <nav className="px-5 sm:px-8 lg:px-12 py-4 border-b border-slate-200/60 bg-white/80 backdrop-blur sticky top-0 z-40">
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5"><Logo size={36} withWordmark /></Link>
        <Link to="/login" className="text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="invite-signin-link">Sign in</Link>
      </div>
    </nav>
  );
}

function Tile({ icon: Icon, label, value, accent = false, cap = false }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-3 bg-slate-50/60">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-semibold text-slate-900 mt-1 break-words ${accent ? "text-xl text-[#C75D33] font-display" : ""} ${cap ? "capitalize" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function Benefit({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-3">
      <div className="w-9 h-9 shrink-0 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="font-bold text-slate-900">{title}</div>
        <div className="text-xs text-slate-600 leading-relaxed mt-0.5">{desc}</div>
      </div>
    </div>
  );
}
